import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  BarChart3,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Wallet,
  AlertTriangle,
  Clock,
  Receipt,
  Inbox,
  Calendar,
  Info,
  Repeat,
  CreditCard,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { supabase } from '../lib/supabase';
import { useNotifications } from '../store/useStore';
import { formatCurrency, formatDate } from '../lib/exportUtils';
import { cn } from '../lib/cn';
import { oggiRoma } from '../lib/dataItalia';
import {
  MESI_BREVI,
  TIPI_FISCALI,
  aggregaFatture,
  mesiAnno,
  statisticheAbbonamenti,
  type AbbonamentoAnalisi,
  type CostoAnalisi,
  type FatturaAnalisi,
} from '../lib/dashboard/kpiFatturato';

export default function AnalisiAziendaliPage() {
  const { addNotification } = useNotifications();

  const [invoices, setInvoices] = useState<FatturaAnalisi[]>([]);
  const [passive, setPassive] = useState<CostoAnalisi[]>([]);
  const [subscriptions, setSubscriptions] = useState<AbbonamentoAnalisi[]>([]);
  const [passiveError, setPassiveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const now = useMemo(() => new Date(), []);
  const currentYear = now.getFullYear();
  const [year, setYear] = useState<number>(currentYear);

  const [view, setView] = useState<'panoramica'>('panoramica');

  const load = useCallback(async () => {
    setLoading(true);
    setPassiveError(null);

    // Fatture attive emesse + relative scadenze
    const invQuery = supabase
      .from('invoices')
      .select(
        'id, invoice_number, invoice_date, document_type, status, payment_status, payment_due_date, total_amount, taxable_amount, customers(company_name), invoice_installments(amount, due_date, paid_at, payment_status, installment_number)'
      )
      .eq('status', 'issued');

    // Fatture passive: ora stanno nel database, allineate ogni notte da
    // Fatture in Cloud. Prima si rileggevano al volo dal fornitore di
    // fatturazione elettronica a ogni apertura della pagina.
    const passivePromise = supabase
      .from('passive_invoices')
      .select('total_amount, taxable_amount, document_type, document_date')
      // Le fatture ancora "da registrare" su Fatture in Cloud non sono costi.
      .is('fic_pending_id', null);

    // Subscriptions attive (ricavo ricorrente — solo informativo)
    const subQuery = supabase
      .from('customer_subscriptions')
      .select('monthly_amount, status, next_renewal_date');

    const [{ data: invData, error: invError }, passiveRes, { data: subData }] = await Promise.all([
      invQuery,
      passivePromise,
      subQuery,
    ]);

    if (invError) {
      addNotification({ type: 'error', title: 'Errore', message: 'Impossibile caricare le fatture attive' });
    } else {
      setInvoices(((invData as unknown) as FatturaAnalisi[]).filter((i) => TIPI_FISCALI.has(i.document_type)));
    }

    setSubscriptions(((subData as unknown) as AbbonamentoAnalisi[]) || []);

    if (passiveRes.error) {
      setPassive([]);
      setPassiveError(passiveRes.error.message || 'Fatture passive non disponibili');
    } else {
      setPassive((passiveRes.data ?? []) as CostoAnalisi[]);
    }

    setLoading(false);
  }, [addNotification]);

  useEffect(() => {
    load();
  }, [load]);

  // La data italiana: con quella di Londra, fra le 22 e mezzanotte una rata
  // in scadenza oggi risultava gia' scaduta.
  const todayStr = useMemo(() => oggiRoma(now), [now]);

  // ---- Aggregazione per anno/mese (src/lib/dashboard/kpiFatturato.ts) ----
  const data = useMemo(
    () => aggregaFatture(invoices, passive, { oggi: todayStr, annoCorrente: currentYear }),
    [invoices, passive, currentYear, todayStr]
  );

  const emessoMonths = mesiAnno(data.emessoPerAnno, year);
  const incassatoMonths = mesiAnno(data.incassatoPerAnno, year);
  const passiveMonths = mesiAnno(data.costiPerAnno, year);

  const sum = (arr: number[]) => arr.reduce((s, v) => s + v, 0);
  const totEmesso = sum(emessoMonths);
  const totIncassato = sum(incassatoMonths);
  const totPassive = sum(passiveMonths);
  const margineCassa = totIncassato - totPassive;

  const chartMax = Math.max(1, ...emessoMonths, ...incassatoMonths, ...passiveMonths);

  // ---- Ricavo ricorrente subscriptions (solo informativo, NON sommato al fatturato) ----
  const subStats = useMemo(() => statisticheAbbonamenti(subscriptions, todayStr), [subscriptions, todayStr]);

  // La sezione "Tasse" e' stata rimossa: era una previsione di imposta
  // sostitutiva, contributi INPS e scadenze F24 per una PERSONA FISICA in
  // regime forfettario. Per una s.r.l. (IRES, IRAP, nessun contributo del
  // titolare) quei numeri sarebbero stati semplicemente sbagliati, ma
  // presentati come una stima attendibile in cruscotto.

  const kpis = [
    { label: `Fatturato ${year}`, value: formatCurrency(totEmesso), icon: Receipt, color: 'text-primary-500', hint: 'Imponibile emesso (competenza)' },
    { label: `Incassato ${year}`, value: formatCurrency(totIncassato), icon: Wallet, color: 'text-green-500', hint: 'Per data pagamento (cassa)' },
    { label: `Costi ${year}`, value: formatCurrency(totPassive), icon: Inbox, color: 'text-pink-500', hint: 'Fornitori, senza IVA; note di credito sottratte' },
    { label: `Margine di cassa`, value: formatCurrency(margineCassa), icon: margineCassa >= 0 ? TrendingUp : TrendingDown, color: margineCassa >= 0 ? 'text-emerald-500' : 'text-red-500', hint: 'Incassato − costi' },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Analisi aziendali</h1>
          <p className="text-sm text-neutral-400 mt-1">
            Fatturato, incassi e costi, tutti senza IVA
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="h-10 w-28 bg-white border-gray-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {data.anniDisponibili.map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="h-10">
            <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* Tabs Panoramica / Tasse */}
      <div className="flex gap-2">
        {([
          { key: 'panoramica', label: 'Panoramica', icon: BarChart3 },
        ] as const).map((t) => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2',
              view === t.key ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {view === 'panoramica' && (
      <>
      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        {kpis.map((kpi, i) => (
          <Card key={i} className="border-gray-200 bg-white">
            <CardContent className="p-3 md:p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] md:text-sm font-medium text-neutral-500 uppercase tracking-wider">{kpi.label}</p>
                <kpi.icon className={cn('h-3 w-3 md:h-4 md:w-4', kpi.color)} />
              </div>
              <div className="text-sm md:text-2xl font-bold text-gray-900 truncate">{kpi.value}</div>
              <p className="text-[10px] text-neutral-400 mt-1 hidden md:block">{kpi.hint}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {passiveError && (
        <p className="text-xs text-amber-600 flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> Fatture passive non caricate: {passiveError}
        </p>
      )}

      {/* Crediti da incassare (snapshot attuale) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
        <Card className="border-gray-200 bg-white">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Da incassare</p>
              <Clock className="h-4 w-4 text-primary-500" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-gray-900">{formatCurrency(data.aperto.totale)}</div>
            <p className="text-[10px] text-neutral-400 mt-1">Crediti aperti (tutte le annualità)</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-red-600 uppercase tracking-wider">Scaduto</p>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-red-700">{formatCurrency(data.aperto.scaduto)}</div>
            <p className="text-[10px] text-red-500 mt-1">Scadenze già superate, non saldate</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">A scadere</p>
              <Calendar className="h-4 w-4 text-amber-500" />
            </div>
            <div className="text-xl md:text-2xl font-bold text-amber-700">{formatCurrency(data.aperto.futuro)}</div>
            <p className="text-[10px] text-amber-500 mt-1">Scadenze future ancora aperte</p>
          </CardContent>
        </Card>
      </div>

      {/* Cashflow mensile */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-4">
            <div>
              <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-primary-500" /> Andamento mensile {year}
              </h2>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary-500" /> Emesso</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-500" /> Incassato</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-pink-500" /> Costi</span>
            </div>
          </div>

          <div className="flex items-end gap-1 md:gap-2 h-40 md:h-52">
            {MESI_BREVI.map((m, i) => (
              <div key={m} className="flex-1 flex flex-col items-center gap-1 h-full justify-end group">
                <div className="flex items-end justify-center gap-0.5 w-full h-full">
                  {[
                    { v: emessoMonths[i], c: 'bg-primary-500' },
                    { v: incassatoMonths[i], c: 'bg-green-500' },
                    { v: passiveMonths[i], c: 'bg-pink-500' },
                  ].map((bar, bi) => (
                    <div
                      key={bi}
                      className={cn('w-1/4 rounded-t transition-all', bar.c)}
                      style={{ height: `${Math.max(0, (Math.abs(bar.v) / chartMax) * 100)}%` }}
                      title={`${m}: ${formatCurrency(bar.v)}`}
                    />
                  ))}
                </div>
                <span className="text-[9px] md:text-[10px] text-neutral-400">{m}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Ricavo ricorrente (subscriptions) — informativo */}
      {subStats.attivi > 0 && (
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4 md:p-6">
            <div className="flex items-center gap-2 mb-1">
              <Repeat className="w-5 h-5 text-primary-600" />
              <h2 className="text-base md:text-lg font-bold text-gray-900">Ricavo ricorrente (subscriptions)</h2>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 mb-5">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Dato <strong>informativo</strong>: il ricavo dei canoni è già incluso in Fatturato/Incassato quando
                emetti la relativa fattura. Non è sommato qui né nella base imponibile per evitare il doppio conteggio.
              </span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
              {[
                { label: 'MRR', value: formatCurrency(subStats.mrr), icon: Repeat, color: 'text-primary-500', hint: 'Ricavo ricorrente mensile' },
                { label: 'ARR', value: formatCurrency(subStats.arr), icon: TrendingUp, color: 'text-emerald-500', hint: 'Annuo (MRR × 12)' },
                { label: 'Subs attive', value: subStats.attivi, icon: CreditCard, color: 'text-primary-500', hint: 'Sottoscrizioni in corso' },
                { label: 'In scadenza', value: subStats.inScadenza, icon: AlertTriangle, color: 'text-amber-500', hint: 'Rinnovo entro 30 gg' },
              ].map((kpi, i) => (
                <div key={i} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3 md:p-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] md:text-xs font-medium text-neutral-500 uppercase tracking-wider">{kpi.label}</p>
                    <kpi.icon className={cn('h-3 w-3 md:h-4 md:w-4', kpi.color)} />
                  </div>
                  <div className="text-sm md:text-xl font-bold text-gray-900 truncate">{kpi.value}</div>
                  <p className="text-[10px] text-neutral-400 mt-1 hidden md:block">{kpi.hint}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}


      {view === 'panoramica' && (
      <>
      {/* Prossime scadenze incassi */}
      <Card className="bg-white border-gray-200 overflow-hidden">
        <div className="px-4 md:px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary-500" />
          <h2 className="text-base md:text-lg font-bold text-gray-900">Scadenze incassi aperte</h2>
          <span className="text-xs text-neutral-400">({data.scadenzeAperte.length})</span>
        </div>
        {data.scadenzeAperte.length === 0 ? (
          <div className="p-8 text-center text-neutral-400 text-sm">Nessun credito aperto. Tutto incassato 🎉</div>
        ) : (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Fattura</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Scadenza</th>
                  <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Importo</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Stato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.scadenzeAperte.slice(0, 30).map((s, i) => (
                  <tr key={`${s.fatturaId}-${i}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{s.numero || <span className="italic text-neutral-400">Bozza</span>}</td>
                    <td className="px-4 py-3 text-gray-600 truncate max-w-[200px]">{s.cliente}</td>
                    <td className="px-4 py-3 text-gray-600">{s.scadenza ? formatDate(s.scadenza) : '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{formatCurrency(s.importo)}</td>
                    <td className="px-4 py-3">
                      {s.scaduta ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Scaduta</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">A scadere</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.scadenzeAperte.length > 30 && (
              <p className="px-4 py-3 text-xs text-neutral-400 border-t border-gray-100">
                Mostrate le prime 30 di {data.scadenzeAperte.length} scadenze.
              </p>
            )}
          </div>
        )}
      </Card>
      </>
      )}
    </div>
  );
}
